import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import { apiClient } from '../lib/apiClient';
import type { User } from '../types/user';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Set token in API client
      apiClient.setAccessToken(token);

      // Fetch current user
      const userData = await apiClient.auth.me() as User;
      setUser(userData);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('authToken');
      apiClient.clearTokens();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      console.log('🔐 Starting login process...');
      const response = await apiClient.auth.login({ email, password }) as { success: boolean; data: { accessToken: string; refreshToken: string; user: User } };
      console.log('📥 Login response:', response);
      const { accessToken, user: userData } = response.data;
      console.log('👤 User data:', userData);

      // Store token
      localStorage.setItem('authToken', accessToken);
      apiClient.setAccessToken(accessToken);

      // Set user
      setUser(userData);
      console.log('✅ User set in context');
    } catch (error: any) {
      console.error('❌ Login error:', error);
      throw new Error(error.message || 'Login failed');
    }
  };

  const register = async (data: RegisterData) => {
    try {
      const response = await apiClient.auth.register(data) as { success: boolean; data: { accessToken: string; refreshToken: string; user: User } };
      const { accessToken, user: userData } = response.data;

      // Store token
      localStorage.setItem('authToken', accessToken);
      apiClient.setAccessToken(accessToken);

      // Set user
      setUser(userData);
    } catch (error: any) {
      throw new Error(error.message || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      await apiClient.auth.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      // Clear local state regardless of API response
      localStorage.removeItem('authToken');
      apiClient.clearTokens();
      setUser(null);
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Alias for backwards compatibility
export const useAuthContext = useAuth;
